import { DataSource, CollectionViewer } from "@angular/cdk/collections";
import { HttpParams } from '@angular/common/http';
import { Observable, Subscription, BehaviorSubject, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { VedraxApiService } from '../../services/vedrax-api.service';
import { DescriptorPage } from '../../descriptor/descriptor-page';
import { DescriptorTable } from '../../descriptor/descriptor-table';
import { NVP } from '../../shared/nvp';

/**
 * Class that implements the {@link DataSource}.
 */
export class VedraxTableDataSource extends DataSource<any[]>{

    /**
     * The subscription
     */
    private subscription: Subscription = new Subscription();

    /**
     * The data listener
     */
    private dataSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /**
     * The total item listener used for pagination
     */
    private totalItemsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * The submitted flag listener
     */
    private submittedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * The descriptor table
     */
    private descriptorTable: DescriptorTable = new DescriptorTable();

    /**
     * An observable total items
     */
    public totalItems$ = this.totalItemsSubject.asObservable();

    /**
     * An observable submitted flag
     */
    public submitted$ = this.submittedSubject.asObservable();

    /**
     * The data retrieved from the listener
     */
    get data(): any[] {
        return this.dataSubject.value;
    }

    constructor(private apiService: VedraxApiService) {
        super();
    }

    /**
     * Method for setting the descriptor table
     * @param descriptorTable 
     */
    setDescriptorTable(descriptorTable: DescriptorTable){
        this.descriptorTable = descriptorTable;
    }

    /**
     * Method used for connection
     * @param collectionViewer 
     * @returns 
     */
    connect(collectionViewer: CollectionViewer): Observable<any[]> {
        return this.dataSubject.asObservable();
    }

    /**
     * Method used for disconnection
     * @param collectionViewer 
     */
    disconnect(collectionViewer: CollectionViewer): void {
        this.dataSubject.complete();
        this.submittedSubject.complete();
        this.totalItemsSubject.complete();
        this.subscription.unsubscribe();
    }

    /**
     * Method used for loading paginated data from API
     * @param endpoint 
     * @param params 
     */
    loadWithPagination(endpoint: string = '', params?: HttpParams) {
        this.submittedSubject.next(true);

        this.subscription.add(
            this.apiService.get<DescriptorPage>(endpoint, params)
                .pipe(
                    catchError(() => of(new DescriptorPage())),
                    finalize(() => this.submittedSubject.next(false))
                )
                .subscribe(page => {
                    this.addItems(page.content);
                    this.totalItemsSubject.next(page.totalElements);
                }));
    }
    
    /**
     * method used for loading data from API
     * @param endpoint 
     * @param params 
     */
    load(endpoint: string, params?: HttpParams) {
        this.submittedSubject.next(true);

        this.subscription.add(
            this.apiService.get<any[]>(endpoint, params)
                .pipe(
                    catchError(() => of([])),
                    finalize(() => this.submittedSubject.next(false))
                )
                .subscribe(list => {
                    this.addItems(list);
                }));

    }

    /**
     * Method used for adding an item
     * @param item 
     */
    addItem(item: any) {
        if (item && item.id) {
            this.transformItemWithMapping(item);
            this.dataSubject.next([...this.data, item]);
        }
    }

    /**
     * Method used for adding items
     * @param items 
     */
    addItems(items: any[] = []) {
        this.transformItemsWithMapping(items);
        this.dataSubject.next(items);
    }

    /**
     * Transform when boolean
     * @param items 
     */
    private transformItemsWithMapping(items: any[] = []) {

        this.descriptorTable.columns
            .filter(column => column.mapping)
            .forEach(column => {
                items.map(item => {
                    const key: string = String(item[column.id]);//in case of boolean
                    item[column.id] = this.findValueMapping(column.mapping, key);
                })
            });

    }

    /**
     * Transform when boolean
     * @param items 
     */
    private transformItemWithMapping(item: any) {

        this.descriptorTable.columns
            .filter(column => column.mapping)
            .forEach(column => {
                const key: string = String(item[column.id]);//in case of boolean
                item[column.id] = this.findValueMapping(column.mapping, key);
            });

    }

    /**
     * Method for finding mapping value
     * @param mapping 
     * @param key 
     * @returns 
     */
    private findValueMapping(mapping: NVP[] = [], key: string): string {
        const element = mapping.find(element => element.key === key);
        return element ? String(element.value) : '';
    }

    /**
    * Method for updating a specific item
    * @param item the item to be updated
    */
    updateItem(item: any = {}) {
        const idx = this.data.findIndex(x => x.id === item.id);
        this.transformItemWithMapping(item);
        Object.assign(this.data[idx], item);
    }

}